/**
 * SimpleValueDisplay - Component for displaying simple key-value pairs
 *
 * Displays formatted values in a clean VStack layout:
 * **[Bold Label]: Value**
 *
 * Used for simple data displays like Millage and DRF configurations.
 *
 * Usage:
 * ```tsx
 * <SimpleValueDisplay
 *   values={[
 *     { label: "Peak Millage", value: "7.50" },
 *     { label: "Minimum Millage", value: "0.40" }
 *   ]}
 * />
 * ```
 */

import { Box, Text, VStack } from '@chakra-ui/react';
import type React from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';

export interface SimpleValueItem {
  /** Label for the value */
  label: string;

  /** Value to display */
  value: string | number;

  /** Optional formatter function for the value */
  formatter?: (value: string | number) => string;
}

export interface SimpleValueDisplayProps {
  /** Array of label-value pairs to display */
  values: SimpleValueItem[];

  /** Optional title for the display */
  title?: string;
}

export const SimpleValueDisplay: React.FC<SimpleValueDisplayProps> = ({ values, title }) => {
  const formatValue = (item: SimpleValueItem): string => {
    if (item.formatter) {
      return item.formatter(item.value);
    }
    return String(item.value);
  };

  return (
    <Box p={LAYOUT_SPACING.CARD_PADDING}>
      {title && (
        <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={LAYOUT_SPACING.CONTENT_GAP}>
          {title}
        </Text>
      )}
      <VStack align="stretch" gap={LAYOUT_SPACING.CONTENT_GAP}>
        {values.map((item) => (
          <Box key={item.label} py={LAYOUT_SPACING.ELEMENT_GAP / 2}>
            <Text fontSize="sm" color="gray.800" lineHeight="1.6">
              <Text as="span" fontWeight="semibold" color="gray.900">
                {item.label}
              </Text>
              :{' '}
              <Text as="span" ml={1}>
                {formatValue(item)}
              </Text>
            </Text>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};
